// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import shareEntity from "@insite/client-framework/Store/CommonHandlers/ShareEntity";
import translate from "@insite/client-framework/Translate";
import { ShareEntityModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import ShareFill from "@store/Library/Components/Toolkit/Icons/Share/ShareFill";
import { formGridProps } from "@store/Themes/common/props/form";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import getInvalidFieldMessage from "@toolkit/Framework/Common/Utilities/forms/getInvalidFieldMessage";
import getRequiredFieldMessage from "@toolkit/Framework/Common/Utilities/forms/getRequiredFieldMessage";
/* Starter Custom Code - End */

interface OwnProps {
    entityId: string;
    entityName: "Order" | "Invoice";
    extendedStyles?: ShareEntityButtonStyles;
    variant?: "button" | "clickable";
    /**
     * If true, the email attachment will be generated using the entity details web page, rather than a pre-defined PDF file.
     */
    generateAttachmentFromWebpage?: boolean;
    extraProperties?: SafeDictionary<string>;
}

const mapDispatchToProps = {
    shareEntity,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ShareEntityButtonStyles {
    clickable?: ClickablePresentationProps;
    button?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    emailToGridItem?: GridItemProps;
    emailToTextField?: TextFieldProps;
    emailFromGridItem?: GridItemProps;
    emailFromTextField?: TextFieldProps;
    subjectGridItem?: GridItemProps;
    subjectTextField?: TextFieldProps;
    messageGridItem?: GridItemProps;
    messageTextArea?: TextAreaProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    sendButton?: ButtonPresentationProps;
    // Starter Custom Code
    link?: LinkPresentationProps;
}

export const shareEntityButtonStyles: ShareEntityButtonStyles = {
    container: formGridProps.gridContainer,
    button: { variant: "tertiary" },
    link: linkGetActionLinkProps(ShareFill),
    modal: { sizeVariant: "medium" },
    emailToGridItem: { width: [12, 6, 6, 6, 6] },
    emailFromGridItem: { width: [12, 6, 6, 6, 6] },
    subjectGridItem: { width: 12 },
    messageGridItem: { width: 12 },
    cancelButton: { variant: "secondary" },
};

const invoiceUrl = "/api/v1/invoices/shareinvoice";
const orderUrl = "/api/v1/orders/shareorder";
const ShareEntityButton = ({
    entityId,
    entityName,
    extendedStyles,
    shareEntity,
    variant = "button",
    generateAttachmentFromWebpage,
    extraProperties,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(shareEntityButtonStyles, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailToError, setEmailToError] = useState("");
    const [emailFrom, setEmailFrom] = useState("");
    const [emailFromError, setEmailFromError] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);

    /* Starter Custom Code - Start */
    const emailToLabel = translate("Email To");
    const emailFromLabel = translate("Email From");
    /* Starter Custom Code - End */

    if (!entityId) {
        return null;
    }

    let url = "";

    if (entityName === "Order") {
        url = orderUrl;
    } else if (entityName === "Invoice") {
        url = invoiceUrl;
    }

    // Starter Removed - requiredFieldMessage
    // Starter Removed - emailFieldMessage
    const checkForErrors = () => {
        const localEmailToError = !emailTo
            ? getRequiredFieldMessage(emailToLabel) // Starter Custom Code
            : validateEmail(emailTo)
              ? ""
              : getInvalidFieldMessage(emailToLabel); // Starter Custom Code
        setEmailToError(localEmailToError);
        const localEmailFromError = !emailFrom
            ? getRequiredFieldMessage(emailFromLabel) // Starter Custom Code
            : validateEmail(emailFrom)
              ? ""
              : getInvalidFieldMessage(emailFromLabel); // Starter Custom Code
        setEmailFromError(localEmailFromError);

        return !!(localEmailToError || localEmailFromError);
    };

    const resetFields = () => {
        setEmailTo("");
        setEmailToError("");
        setEmailFrom("");
        setEmailFromError("");
        setSubject(`${translate(`${entityName}`)} #${entityId}`);
        setMessage("");
    };

    const emailButtonClickHandler = () => {
        resetFields();
        setModalIsOpen(true);
    };

    const emailToChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailTo(event.target.value);
        setEmailToError(!event.target.value.trim() ? getRequiredFieldMessage(emailToLabel) : ""); // Starter Custom Code
    };

    const emailFromChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailFrom(event.target.value);
        setEmailFromError(!event.target.value.trim() ? getRequiredFieldMessage(emailFromLabel) : ""); // Starter Custom Code
    };

    const subjectChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubject(event.target.value);
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const sendButtonClickHandler = () => {
        if (checkForErrors()) {
            return;
        }

        setIsSending(true);
        shareEntity({
            shareEntityModel: {
                entityId,
                entityName,
                emailTo,
                emailFrom,
                subject,
                message,
                ...extraProperties,
            } as ShareEntityModel,
            url,
            onSuccess: () => {
                setModalIsOpen(false);
                setIsSending(false);
                toasterContext.addToast({ body: siteMessage("Entity_Share_Success"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
            generateAttachmentFromWebpage,
        });
    };

    return (
        <>
            {variant === "clickable" && (
                /* Starter Custom Code - Start */
                <Link {...styles.link} onClick={emailButtonClickHandler} data-test-selector="shareEntity_email">
                    {translate("Share")}
                </Link>
                /* Starter Custom Code - End */
            )}
            {variant === "button" && (
                <Button {...styles.button} onClick={emailButtonClickHandler} data-test-selector="shareEntity_email">
                    {translate("Share") /* Starter Custom Code */}
                </Button>
            )}
            <Modal
                {...styles.modal}
                headline={`${translate(`Share ${entityName}`)} #${entityId}` /* Starter Custom Code */}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <FormWrapper onlyFocus /* Starter Custom Code */>
                    <GridContainer {...styles.container} data-test-selector="shareEntity_modal">
                        <GridItem {...styles.emailToGridItem}>
                            <TextField
                                label={emailToLabel} // Starter Custom Code
                                required
                                value={emailTo}
                                error={emailToError}
                                onChange={emailToChangeHandler}
                                data-test-selector="shareEntity_emailTo"
                                {...styles.emailToTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.emailFromGridItem}>
                            <TextField
                                label={emailFromLabel} // Starter Custom Code
                                required
                                value={emailFrom}
                                error={emailFromError}
                                onChange={emailFromChangeHandler}
                                data-test-selector="shareEntity_emailFrom"
                                {...styles.emailFromTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.subjectGridItem}>
                            <TextField
                                label={translate("Subject")}
                                value={subject}
                                onChange={subjectChangeHandler}
                                data-test-selector="shareEntity_subject"
                                {...styles.subjectTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.messageGridItem}>
                            <TextArea
                                label={translate("Message")}
                                value={message}
                                onChange={messageChangeHandler}
                                data-test-selector="shareEntity_message"
                                {...styles.messageTextArea}
                            />
                        </GridItem>
                    </GridContainer>
                    {/* Starter Custom Code - Start */}
                    <FormActions>
                        <Button
                            {...styles.sendButton}
                            disabled={isSending}
                            onClick={sendButtonClickHandler}
                            data-test-selector="shareEntity_submit"
                        >
                            {translate("Send")}
                        </Button>
                        <Button
                            {...styles.cancelButton}
                            onClick={modalCloseHandler}
                            data-test-selector="shareEntity_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    </FormActions>
                    {/* Starter Custom Code - End */}
                </FormWrapper>
            </Modal>
        </>
    );
};

export default connect(null, mapDispatchToProps)(ShareEntityButton);
